import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import useDebounce from '../../../../../hooks/useDebounce';
import { FiltersBoxValue } from '../Components/FiltersBox/FiltersBox';
import { getErrorMessage } from '../getErrorMessage';
import { mapMonitoramentoToMonitoringItem } from '../mappers';
import { notify } from '../notify';
import { deleteMonitoramento, getMonitoramentos, patchMonitoramentoStatus, removeMonitoramentoCompartilhamento } from '../services';
import { MonitoramentoPageResponse } from '../types';

const DEFAULT_PAGE_SIZE = 20;

export const useMonitoramentos = () => {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<FiltersBoxValue>({});
  const [searchInput, setSearchInput] = useState('');
  const [nome, setNome] = useState('');
  const [ativoFilter, setAtivoFilter] = useState<boolean | undefined>(undefined);
  const [emAlertaHojeFilter, setEmAlertaHojeFilter] = useState<boolean | undefined>(undefined);
  const [pendingToggleIds, setPendingToggleIds] = useState<Set<number>>(new Set());
  const debounce = useDebounce(400);
  const queryClient = useQueryClient();
  const queryKey = ['monitoramentos', { page, size, filters, nome, ativoFilter, emAlertaHojeFilter }];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => getMonitoramentos({ page, size, nome: nome || undefined, ativo: ativoFilter, emAlertaHoje: emAlertaHojeFilter, ...filters }),
  });

  const items = data?.data.content.map(mapMonitoramentoToMonitoringItem) ?? [];

  const handleChangePage = (nextPage: number) => {
    setPage(nextPage - 1);
  };

  const handleChangeSize = (nextSize: number) => {
    setSize(nextSize);
    setPage(0);
  };

  const handleChangeFilters = (nextFilters: FiltersBoxValue) => {
    setFilters(nextFilters);
    setPage(0);
  };

  const handleChangeSearch = (value: string) => {
    setSearchInput(value);
    debounce(() => {
      setNome(value);
      setPage(0);
    });
  };

  const handleChangeAtivoFilter = (value: boolean | undefined) => {
    setAtivoFilter(value);
    setPage(0);
  };

  const handleToggleEmAlertaHojeFilter = () => {
    setEmAlertaHojeFilter(current => (current ? undefined : true));
    setPage(0);
  };

  const { mutate: toggleActive } = useMutation({
    mutationFn: ({ id, ativo }: { id: number; ativo: boolean }) => patchMonitoramentoStatus(id, ativo),
    onMutate: async ({ id, ativo }) => {
      setPendingToggleIds(prev => new Set(prev).add(id));
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<{ data: MonitoramentoPageResponse }>(queryKey);

      queryClient.setQueryData<{ data: MonitoramentoPageResponse }>(queryKey, current =>
        current
          ? {
              ...current,
              data: { ...current.data, content: current.data.content.map(item => (item.id === id ? { ...item, ativo } : item)) },
            }
          : current,
      );

      return { previousData };
    },
    onSuccess: (_data, { ativo }) => {
      notify('success', 5000, ativo ? 'Monitoramento ativado com sucesso.' : 'Monitoramento inativado com sucesso.');
    },
    onError: (error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
      notify('error', 5000, getErrorMessage(error, 'Erro ao atualizar status do monitoramento.'));
    },
    onSettled: (_data, _error, { id }) => {
      setPendingToggleIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
  });

  const handleToggleActive = (id: number, ativo: boolean) => {
    toggleActive({ id, ativo });
  };

  const { mutate: removeMonitoramento } = useMutation({
    mutationFn: (id: number) => deleteMonitoramento(id),
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<{ data: MonitoramentoPageResponse }>(queryKey);

      queryClient.setQueryData<{ data: MonitoramentoPageResponse }>(queryKey, current =>
        current
          ? {
              ...current,
              data: {
                ...current.data,
                content: current.data.content.filter(item => item.id !== id),
                page: { ...current.data.page, totalElements: Math.max(0, current.data.page.totalElements - 1) },
              },
            }
          : current,
      );

      return { previousData };
    },
    onSuccess: () => {
      notify('success', 5000, 'Monitoramento excluído com sucesso.');
    },
    onError: (error, _id, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
      notify('error', 5000, getErrorMessage(error, 'Erro ao excluir monitoramento.'));
    },
  });

  const handleDeleteMonitoramento = (id: number) => {
    removeMonitoramento(id);
  };

  const { mutate: removeForMe } = useMutation({
    mutationFn: (id: number) => removeMonitoramentoCompartilhamento(id),
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<{ data: MonitoramentoPageResponse }>(queryKey);

      queryClient.setQueryData<{ data: MonitoramentoPageResponse }>(queryKey, current =>
        current
          ? {
              ...current,
              data: {
                ...current.data,
                content: current.data.content.filter(item => item.id !== id),
                page: { ...current.data.page, totalElements: Math.max(0, current.data.page.totalElements - 1) },
              },
            }
          : current,
      );

      return { previousData };
    },
    onSuccess: () => {
      notify('success', 5000, 'Monitoramento removido da sua Central.');
    },
    onError: (error, _id, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
      notify('error', 5000, getErrorMessage(error, 'Não foi possível remover esse monitoramento da sua Central.'));
    },
  });

  const handleRemoveForMe = (id: number) => {
    removeForMe(id);
  };

  return {
    items,
    isLoading,
    page,
    size,
    filters,
    searchValue: searchInput,
    ativoFilter,
    emAlertaHojeFilter,
    totalElements: data?.data.page.totalElements ?? 0,
    pendingToggleIds,
    onChangePage: handleChangePage,
    onChangeSize: handleChangeSize,
    onChangeFilters: handleChangeFilters,
    onChangeSearch: handleChangeSearch,
    onChangeAtivoFilter: handleChangeAtivoFilter,
    onToggleActive: handleToggleActive,
    onToggleEmAlertaHojeFilter: handleToggleEmAlertaHojeFilter,
    onDeleteMonitoramento: handleDeleteMonitoramento,
    onRemoveForMe: handleRemoveForMe,
  };
};
